/**
 * SaveCandidateButton — star-shaped shortlist toggle for employer cards.
 *
 * Props:
 *   · publicId          — the résumé's public_id (required)
 *   · initialSaved      — bool, hint for optimistic render (default undefined,
 *                         component fetches its own status lazily)
 *   · initialTag        — string tag if already saved (default undefined)
 *   · variant           — "icon" (24×24 star, corner of card) | "pill"
 *                         (rounded button with label) | "large" (résumé page)
 *   · onChange          — (bool) => void, notifies parent on save/unsave
 *   · testIdPrefix      — data-testid prefix (default "save-candidate")
 *
 * Behavior:
 *   · Optimistic — flips the star immediately, rolls back on error.
 *   · Uses POST /api/employer/shortlist/{publicId} + DELETE.
 *   · Prevents parent Link navigation (stopPropagation).
 */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Star, Loader, Lock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const NAVY = "#0F1B2D";
const MUSTARD = "#FFC145";

export default function SaveCandidateButton({
  publicId,
  initialSaved = false,
  initialTag = null,
  variant = "icon",
  onChange = () => {},
  testIdPrefix = "save-candidate",
  readOnly = false,          // when true (e.g. admin impersonation), the
                             // button renders as a locked star with a
                             // tooltip explaining why — no fetch fired.
}) {
  const [saved, setSaved] = useState(!!initialSaved);
  const [tag, setTag] = useState(initialTag);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Sync internal state when the parent's async fetch updates `initialSaved`
  // (e.g. after the batch shortlist-status call resolves on Store search).
  // Without this, the star renders as unsaved on page reload even though the
  // batch endpoint returns saved:[public_id].
  useEffect(() => { setSaved(!!initialSaved); }, [initialSaved]);
  useEffect(() => { setTag(initialTag); }, [initialTag]);

  const toggle = useCallback(async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (readOnly) {
      // Show a brief inline warning instead of firing the request.
      setError("Read-only session — exit impersonation to save.");
      setTimeout(() => setError(""), 2400);
      return;
    }
    if (!publicId || busy) return;
    const wasSaved = saved;
    // Optimistic flip
    setSaved(!wasSaved);
    setBusy(true);
    setError("");
    try {
      if (wasSaved) {
        await axios.delete(`${BACKEND_URL}/api/employer/shortlist/${publicId}`,
          { withCredentials: true });
        setTag(null);
      } else {
        await axios.post(`${BACKEND_URL}/api/employer/shortlist/${publicId}`,
          {}, { withCredentials: true });
      }
      onChange(!wasSaved);
    } catch (err) {
      // Roll back
      setSaved(wasSaved);
      const msg = err?.response?.data?.detail || "Save failed.";
      setError(msg);
      // Clear the error toast after a beat
      setTimeout(() => setError(""), 2200);
    } finally {
      setBusy(false);
    }
  }, [publicId, saved, busy, onChange, readOnly]);

  // Variant styling
  const iconOnly = variant === "icon";
  const isPill  = variant === "pill";
  const isLarge = variant === "large";

  const size = isLarge ? 18 : 13;
  const buttonStyle = {
    background: saved ? MUSTARD : "white",
    border: `1.5px solid ${NAVY}`,
    color: NAVY,
    cursor: busy ? "wait" : "pointer",
    boxShadow: `2px 2px 0 0 ${saved ? NAVY : "#E2E8F0"}`,
    transition: "background 120ms ease",
  };

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={toggle}
        aria-pressed={saved}
        aria-label={saved ? "Remove from shortlist" : "Save to shortlist"}
        title={saved ? "Saved · click to remove" : "Save to shortlist"}
        className={
          iconOnly
            ? "inline-flex items-center justify-center rounded"
            : "inline-flex items-center gap-1 rounded font-mono-rm uppercase tracking-widest"
        }
        style={{
          ...buttonStyle,
          padding: iconOnly ? "6px" : (isLarge ? "8px 12px" : "4px 8px"),
          fontSize: isLarge ? 12 : 10,
          fontWeight: 700,
        }}
        data-testid={`${testIdPrefix}-${publicId}`}
      >
        {busy ? (
          <Loader size={size} className="animate-spin" />
        ) : readOnly ? (
          <Lock size={size} style={{ color: NAVY, opacity: 0.6 }} />
        ) : (
          <Star
            size={size}
            fill={saved ? NAVY : "none"}
            style={{ color: NAVY }}
          />
        )}
        {isPill && <span>{readOnly ? "Locked" : (saved ? "Saved" : "Save")}</span>}
        {isLarge && <span>{readOnly ? "Read-only session" : (saved ? "Saved" : "Save to shortlist")}</span>}
      </button>
      {error && (
        <div
          className="absolute z-30 mt-1 top-full right-0 rounded px-2 py-1 text-[10px] font-mono-rm uppercase tracking-widest"
          style={{ background: "#FEE2E2", color: "#7F1D1D", border: "1px solid #FCA5A5", whiteSpace: "nowrap" }}
          data-testid={`${testIdPrefix}-${publicId}-error`}
        >
          {error}
        </div>
      )}
      {tag && saved && !iconOnly && (
        <span
          className="ml-1 inline-block text-[9px] font-mono-rm uppercase tracking-widest px-1 py-0.5 rounded"
          style={{ background: NAVY, color: MUSTARD, fontWeight: 700 }}
        >
          {tag}
        </span>
      )}
    </div>
  );
}
